import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});

/* ================= GET ALL COUNTRY LANGUAGE ================= */

export const fetchCountryLanguages = createAsyncThunk(
  "countryLanguage/fetchCountryLanguages",
  async ({ search = "", page = 1, limit = 10 }, thunkAPI) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/get-all-country-languages?search=${search}&page=${page}&limit=${limit}`,
        {
          headers: getHeaders(),
        }
      );

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ================= GET COMBINED CODE ================= */

export const fetchCombinedCode = createAsyncThunk(
  "countryLanguage/fetchCombinedCode",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/get-combined-code`, {
        headers: getHeaders()
      });

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ================= ADD COUNTRY LANGUAGE ================= */

export const addCountryLanguage = createAsyncThunk(
  "countryLanguage/addCountryLanguage",
  async (data, thunkAPI) => {
    try {
      const payload = {
        country: data.country,
        language: data.language,
        combined_code: data.combined_code,
      };

      const res = await axios.post(
        `${BASE_URL}/add-country-language`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ================= UPDATE COUNTRY LANGUAGE ================= */

export const updateCountryLanguage = createAsyncThunk(
  "countryLanguage/updateCountryLanguage",
  async ({ id, data, ...rest }, thunkAPI) => {
    try {
      const formData = data || rest;
      const payload = {
        id,
        country: formData.country,
        language: formData.language,
        combined_code: formData.combined_code,
      };

      const res = await axios.put(
        `${BASE_URL}/update-country-language`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ================= DELETE COUNTRY LANGUAGE ================= */

export const deleteCountryLanguage = createAsyncThunk(
  "countryLanguage/deleteCountryLanguage",
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/remove-country-language/${id}`, {
        headers: getHeaders(),
      });

      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ================= COUNTRY LANGUAGE USED ================= */

export const checkCountryLanguageUsed = createAsyncThunk(
  "countryLanguage/checkCountryLanguageUsed",
  async (id, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/country-language-used/${id}`, {
        headers: getHeaders(),
      });

      return res.data.data.is_country_language_used;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

const countryLanguageSlice = createSlice({
  name: "countryLanguage",

  initialState: {
    countryLanguages: [],
    dropdownList:[],

    page: 1,
    limit: 10,
    total: 0,

    loading: false,
    saving: false,
    deleting: false,
    checkingUsed: false,
    isCountryLanguageUsed: false,

    error: null,
  },

  reducers: {
    clearSelectedCountryLanguage: (state) => {
      state.selectedCountryLanguage = null;
    },
  },

  extraReducers: (builder) => {
    /* ===== GET ALL ===== */

    builder
      .addCase(fetchCountryLanguages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCountryLanguages.fulfilled, (state, action) => {
        state.loading = false;
        state.countryLanguages = action.payload.items || [];
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
      })
      .addCase(fetchCountryLanguages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    /* ===== COMBINED CODE ===== */

    builder
      .addCase(fetchCombinedCode.fulfilled, (state, action) => {
        state.dropdownList = action.payload;
      })

    /* ===== ADD ===== */
    builder
      .addCase(addCountryLanguage.pending, (state) => {
        state.saving = true;
      })
      .addCase(addCountryLanguage.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(addCountryLanguage.rejected, (state) => {
        state.saving = false;
      });

    /* ===== UPDATE ===== */
    builder
      .addCase(updateCountryLanguage.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateCountryLanguage.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateCountryLanguage.rejected, (state) => {
        state.saving = false;
      });

    /* ===== DELETE ===== */
    builder
      .addCase(deleteCountryLanguage.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteCountryLanguage.fulfilled, (state, action) => {
        state.deleting = false;
        state.countryLanguages = state.countryLanguages.filter(
          (item) => item.id !== action.payload
        );
      })
      .addCase(deleteCountryLanguage.rejected, (state) => {
        state.deleting = false;
      });

    /* ===== COUNTRY LANGUAGE USED ===== */
    builder
      .addCase(checkCountryLanguageUsed.pending, (state) => {
        state.checkingUsed = true;
        state.isCountryLanguageUsed = false;
      })
      .addCase(checkCountryLanguageUsed.fulfilled, (state, action) => {
        state.checkingUsed = false;
        state.isCountryLanguageUsed = action.payload;
      })
      .addCase(checkCountryLanguageUsed.rejected, (state, action) => {
        state.checkingUsed = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedCountryLanguage } =
  countryLanguageSlice.actions;

export default countryLanguageSlice.reducer;
